/**
 * useOwnedFloorIds: the floors (locations) the active org OWNS.
 *
 * One rule for "is this floor ours", shared by Products, Inventory and
 * Base garments: not a floor the org reaches as a 3PL client (`is_shared`),
 * and owned by the org (an unset owner is the org's own, as LocationContext
 * leaves it for a single-tenant floor). The ids feed
 * `useConnectedClientsAtLocations`, so a choice that spans every floor the org
 * owns (the "Who can use it" filter, its client names) reads the same set on
 * every page.
 *
 * Reads only what LocationContext already holds: no call. The array keeps its
 * identity until the locations or the org change, so it is safe as a memo or
 * query input; disabled (or no org yet) returns one shared empty array.
 */
import { useMemo } from 'react';

import { useLocation, type Location } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';

const NO_FLOORS: readonly string[] = Object.freeze([]);

/** A floor the org owns (see the module comment); false with no org. */
export function isOwnedFloor(
  location: Pick<Location, 'owner_organization_id' | 'is_shared'>,
  organizationId: string | null | undefined,
): boolean {
  if (!organizationId) return false;
  return !location.is_shared && (location.owner_organization_id ?? organizationId) === organizationId;
}

/** The ids of the selectable floors the org owns; `enabled: false` returns none (and reads nothing new). */
export function useOwnedFloorIds(enabled = true): readonly string[] {
  const { locations } = useLocation();
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? null;
  return useMemo(
    () =>
      enabled && organizationId
        ? locations.filter((location) => isOwnedFloor(location, organizationId)).map((location) => location.id)
        : NO_FLOORS,
    [enabled, organizationId, locations],
  );
}
